import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useStatus } from './hooks/useStatus';
import { Header } from './components/Header';
import { StatusCard } from './components/StatusCard';
import { StatsGrid } from './components/StatsGrid';
import { SitesSection } from './components/SitesSection';
import { ActivityList } from './components/ActivityList';
import { NEMPricesWidget } from './components/NEMPricesWidget';
import { LocationPromptBanner } from './components/LocationPromptBanner';
import { SiteDetail } from './pages/SiteDetail';
import { EnergyHistory } from './pages/EnergyHistory';
import { AdminUsers } from './pages/AdminUsers';
import { AdminSites } from './pages/AdminSites';
import { AdminAudit } from './pages/AdminAudit';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { SiteOnboarding } from './pages/SiteOnboarding';
import { Account } from './pages/Account';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { VerifyEmail } from './pages/VerifyEmail';
import { AcceptInvite } from './pages/AcceptInvite';
import { Contact } from './pages/Contact';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { SystemPerformanceMetrics } from './components/PerformanceMetrics';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import { seedDemoData, getActivityFeed, type ActivityEvent } from './api/client';
import { Loader2, AlertCircle, Database, CheckCircle2, X } from 'lucide-react';
import type { SystemStatus, SitesStatus } from './types';
import { trackPageView, trackEvent } from './utils/analytics';

// Sends a GA4 page_view on every route change (SPA — gtag auto-tracking is off).
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// Protected route component
function ProtectedRoute({ children, requireAdmin = false }: { children: React.ReactNode; requireAdmin?: boolean }) {
  const { user, isAuthenticated, isAdmin, isLoading, refreshUser } = useAuth();
  const location = useLocation();

  // Accounts with no live subscription (social sign-ins that never subscribed,
  // lapsed/cancelled subscriptions) are sent back through registration. Admins
  // are exempt, and /account stays reachable so they can manage or delete the
  // account. The server decides (`needs_subscription`), so nothing is enforced
  // on stacks without Stripe.
  const needsSubscription = !!user?.needs_subscription && !isAdmin;
  const paymentJustCompleted =
    new URLSearchParams(location.search).get('payment') === 'success';

  // Back from Stripe Checkout the webhook can lag by a few seconds; poll
  // /auth/me before deciding they still need to subscribe.
  const [confirmingPayment, setConfirmingPayment] = useState(
    needsSubscription && paymentJustCompleted
  );
  useEffect(() => {
    if (!(needsSubscription && paymentJustCompleted)) {
      setConfirmingPayment(false);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    setConfirmingPayment(true);
    const tick = async () => {
      attempts += 1;
      const fresh = await refreshUser();
      if (cancelled) return;
      if ((fresh && !fresh.needs_subscription) || attempts >= 10) {
        setConfirmingPayment(false);
        return;
      }
      window.setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSubscription, paymentJustCompleted]);

  if (isLoading || confirmingPayment) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
        {confirmingPayment && (
          <p className="text-sm text-gray-600 dark:text-gray-400">Confirming your subscription…</p>
        )}
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsSubscription && location.pathname !== '/account') {
    return <Navigate to="/register?complete=1" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Access Denied
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Admin access required for this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Context for sharing status data
interface StatusContextType {
  status: SystemStatus | null;
  sitesStatus: SitesStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const StatusContext = createContext<StatusContextType | null>(null);

export function useStatusContext() {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatusContext must be used within StatusProvider');
  }
  return context;
}

// User home - redirects regular users to their site or setup page
function UserHome() {
  const { isAdmin } = useAuth();
  const { sitesStatus, loading, error, refresh } = useStatusContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(
    searchParams.get('payment') === 'success'
  );

  // Clear the query param from the URL (without re-render)
  useEffect(() => {
    if (searchParams.get('payment')) {
      if (searchParams.get('payment') === 'success') {
        // Registration funnel: subscription payment completed (conversion).
        trackEvent('sign_up_payment_success');
      }
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // If still loading, show spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  // Payment success banner
  const paymentBanner = showPaymentSuccess ? (
    <div className="mb-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
      <div className="flex items-start">
        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-green-800 dark:text-green-300">
            Payment Successful!
          </h3>
          <p className="mt-1 text-sm text-green-700 dark:text-green-400">
            Welcome to SolanIQ! Your subscription is now active. Your site is being set up and will start optimizing your battery shortly.
          </p>
        </div>
        <button
          onClick={() => setShowPaymentSuccess(false)}
          className="ml-3 text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  ) : null;

  // Admins see the full dashboard
  if (isAdmin) {
    return (
      <>
        {paymentBanner}
        <Dashboard />
      </>
    );
  }

  // The site list failed to load (network blip, API restart). That is not the
  // same as having no sites - without this, an existing user was dropped into
  // the site-setup wizard below and could have created a duplicate site.
  if (!sitesStatus && error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Connection Error
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            We couldn't load your sites. Check your connection and try again.
          </p>
          <button
            onClick={refresh}
            className="mt-4 px-4 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Regular users: check their sites
  const userSites = sitesStatus?.sites || [];

  // No sites - show setup prompt
  if (userSites.length === 0) {
    return (
      <>
        {paymentBanner}
        <NoSitesSetup />
      </>
    );
  }

  // One site - always go straight to its dashboard. No intermediate "Your Sites"
  // card screen, on sign-in or right after registration (the payment-success
  // banner is shown on the dashboard via the ?payment=success query param).
  if (userSites.length === 1) {
    const to = showPaymentSuccess
      ? `/site/${userSites[0].id}?payment=success`
      : `/site/${userSites[0].id}`;
    return <Navigate to={to} replace />;
  }

  // Multiple sites - show a simple site selector
  return (
    <>
      {paymentBanner}
      <UserSitesList sites={userSites} />
    </>
  );
}

// Component shown when user has no sites - renders the onboarding wizard
function NoSitesSetup() {
  return <SiteOnboarding />;
}

// Simple site list for users with multiple sites
function UserSitesList({ sites }: { sites: Array<{ id: string; name: string; active: boolean }> }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Your Sites
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sites.map((site) => (
          <a
            key={site.id}
            href={`/site/${site.id}`}
            className="block p-6 card hover:shadow-lg transition-shadow"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {site.name}
            </h3>
            <p className={`mt-2 text-sm ${
              site.active ? 'text-green-600 dark:text-green-400' :
              'text-gray-500 dark:text-gray-400'
            }`}>
              {site.active ? 'Active' : 'Inactive'}
            </p>
          </a>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const { status, sitesStatus, loading, error, refresh } = useStatusContext();
  const { isAdmin } = useAuth();
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [activityEvents, setActivityEvents] = useState<ActivityEvent[]>([]);

  // Fetch activity feed
  useEffect(() => {
    async function fetchActivity() {
      try {
        const feed = await getActivityFeed(10);
        setActivityEvents(feed.events);
      } catch (err) {
        console.error('Failed to fetch activity feed:', err);
      }
    }
    fetchActivity();
    // Refresh activity every 30 seconds
    const interval = setInterval(fetchActivity, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const result = await seedDemoData({
        users: 3,
        sites_per_user: 2,
        plans_per_site: 5,
      });
      setSeedResult(`Created ${result.created.users} users, ${result.created.sites} sites, ${result.created.optimization_plans} plans`);
      refresh();
    } catch {
      setSeedResult('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand-orange animate-spin mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Connection Error
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{error}</p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
            Make sure the API server is running
          </p>
          <button
            onClick={refresh}
            className="mt-4 px-4 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const isEmpty = status?.stats.users === 0 && status?.stats.sites === 0;

  return (
    <>
      {/* Overall Status Banner */}
      <div
        className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
          status?.overall === 'healthy'
            ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
            : status?.overall === 'degraded'
            ? 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
            : 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
        }`}
      >
        <div>
          <span className="font-medium text-gray-900 dark:text-white">System Status: </span>
          <span
            className={`font-semibold ${
              status?.overall === 'healthy'
                ? 'text-green-700 dark:text-green-400'
                : status?.overall === 'degraded'
                ? 'text-yellow-700 dark:text-yellow-400'
                : 'text-red-700 dark:text-red-400'
            }`}
          >
            {status?.overall?.toUpperCase()}
          </span>
        </div>
        {isEmpty && isAdmin && (
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark disabled:opacity-50 transition-colors"
          >
            {seeding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Database className="w-4 h-4" />
            )}
            {seeding ? 'Seeding...' : 'Seed Demo Data'}
          </button>
        )}
      </div>

      {seedResult && (
        <div className="mb-6 p-3 bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
          {seedResult}
        </div>
      )}

      {/* Stats Grid */}
      {status?.stats && <StatsGrid stats={status.stats} />}

      {/* Services Status */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Service Health
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatusCard
            title="API Server"
            status={status?.api.status || 'unknown'}
          />
          <StatusCard
            title="Database"
            status={status?.database.status || 'unknown'}
            details={
              status?.database.connection
                ? { connection: status.database.connection }
                : undefined
            }
          />
          <StatusCard
            title="Redis"
            status={status?.redis.status || 'unknown'}
            details={
              status?.redis.cached_price_regions !== undefined
                ? { 'Cached Regions': status.redis.cached_price_regions }
                : undefined
            }
          />
          <StatusCard
            title="Optimizer"
            status={status?.optimizer.status || 'unknown'}
            note={status?.optimizer.note}
          />
        </div>
      </section>

      {/* System Performance Metrics */}
      <section className="mt-8">
        <SystemPerformanceMetrics refreshInterval={30000} />
      </section>

      {/* NEM Spot Prices */}
      <section className="mt-8">
        <NEMPricesWidget />
      </section>

      {/* Sites */}
      <SitesSection sites={sitesStatus?.sites || []} sitesPerPage={6} />

      {/* Recent Activity */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Recent Activity
        </h2>
        <ActivityList events={activityEvents} />
      </section>
    </>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  // Only fetch status data when authenticated and auth is not loading
  const statusData = useStatus(30000, isAuthenticated && !authLoading);

  return (
    <StatusContext.Provider value={statusData}>
      <div className="min-h-screen bg-screen">
        {/* Only show header when authenticated */}
        {isAuthenticated && (
          <Header
            onRefresh={statusData.refresh}
            lastUpdated={statusData.status?.timestamp}
          />
        )}

        <main className={isAuthenticated ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" : ""}>
          {/* Asks users whose site has no latitude/longitude to provide it */}
          {isAuthenticated && <LocationPromptBanner />}
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* Public: the needs_subscription redirect lives in ProtectedRoute, so
                an invitee is never sent to subscribe before accepting. */}
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute><UserHome /></ProtectedRoute>} />
            <Route path="/site/:siteId" element={<ProtectedRoute><SiteDetail /></ProtectedRoute>} />
            <Route path="/site/:siteId/energy-history" element={<ProtectedRoute><EnergyHistory /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />

            {/* Admin-only routes */}
            <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/sites" element={<ProtectedRoute requireAdmin><AdminSites /></ProtectedRoute>} />
            <Route path="/admin/audit" element={<ProtectedRoute requireAdmin><AdminAudit /></ProtectedRoute>} />
          </Routes>
        </main>

        {isAuthenticated && (
          <footer className="border-t border-gray-200 dark:border-gray-700 mt-12 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            SolanIQ Battery Optimization Platform &copy; {new Date().getFullYear()}
          </footer>
        )}
      </div>
    </StatusContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ToastProvider>
        <AuthProvider>
          <RouteTracker />
          <AppContent />
        </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
